import { useAtom } from "@dbeining/react-atom";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import React, { useState } from "react";
import { Container } from "react-bootstrap";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import { userAtom } from "../../atoms";
import { TaskComplete, TaskDueToday, TaskOpen, TaskOverdue } from "../../components/common/Badges";
import { ColumnRender, FilterType, IColumn, IFilter, IPagedListAction } from "../../components/UI/Declarations";
import { PagedList } from "../../components/UI/PagedList";
import { TaskStatus } from "../../constants/task/TaskStatus";
import { TaskAddModel } from "../../components/features/task";
import { dateRangeFilters, taskDetailPath } from "../../utils";

type Props = {
    admin?: boolean | undefined;
};

const TaskListPage: React.FC<Props> = ({ admin }) => {
    const navigate = useNavigate();
    const currentUser = useAtom(userAtom);
    const [showAddTask, setShowAddTask] = useState(false);
    const [refreshNumber, setRefeshNumber] = useState(0);

    const taskStatusObject: { value: string; label: string }[] = [];
    for (const status in TaskStatus) {
        if (isNaN(Number(status))) {
            taskStatusObject.push({ value: status, label: status });
        }
    }

    const handleAddTask = (added: boolean) => {
        if (added) {
            setRefeshNumber((n) => n + 1);
        }
        setShowAddTask(false);
    };
    const handleOpenAddTask = () => {
        setShowAddTask(true);
    };

    return (
        <Container fluid>
            {!admin && (
                <>
                    <Helmet>
                        <title>My tasks</title>
                    </Helmet>
                    <h1 className="my-3">My tasks</h1>
                </>
            )}
            <PagedList
                columns={[
                    {
                        member: "name",
                        renderAs: ColumnRender.text,
                        title: "Task",
                        width: 160,
                    } as IColumn,
                    {
                        member: "taskTypeName",
                        renderAs: ColumnRender.text,
                        title: "Type",
                        width: 120,
                    } as IColumn,
                    {
                        member: "location",
                        renderAs: ColumnRender.text,
                        title: "Location",
                        width: 140,
                    } as IColumn,
                    {
                        member: "taskStatus",
                        renderAs: ColumnRender.customRenderer,
                        title: "Task Status",
                        width: 150,
                        customRenderer: (item) => {
                            switch (item.taskStatus?.toString()) {
                                case TaskStatus[TaskStatus.Complete]:
                                    return (
                                        <td>
                                            <TaskComplete />
                                        </td>
                                    );
                                case TaskStatus[TaskStatus.Open]:
                                    return (
                                        <td>
                                            <TaskOpen />
                                        </td>
                                    );
                                case TaskStatus[TaskStatus.DueToday]:
                                    return (
                                        <td>
                                            <TaskDueToday />
                                        </td>
                                    );
                                case TaskStatus[TaskStatus.Overdue]:
                                    return (
                                        <td>
                                            <TaskOverdue />
                                        </td>
                                    );
                                default:
                                    return <td></td>;
                            }
                        },
                    } as IColumn,
                    ...(admin
                        ? ([
                              {
                                  member: "userName",
                                  renderAs: ColumnRender.text,
                                  title: "Personnel",
                                  width: 140,
                              } as IColumn,
                          ] as IColumn[])
                        : []),
                    ...(!admin
                        ? ([
                              {
                                  member: "assignment",
                                  renderAs: ColumnRender.customRenderer,
                                  title: "Created/Assigned",
                                  width: 120,
                                  customRenderer: (item: any) => {
                                      if (
                                          currentUser &&
                                          item.userId?.includes(currentUser.identity.id) &&
                                          item.creatorId === currentUser.identity.id
                                      ) {
                                          return <td>Both</td>;
                                      }
                                      if (currentUser && item.userId?.includes(currentUser.identity.id)) {
                                          return <td>Assigned</td>;
                                      }
                                      if (item.creatorId === currentUser?.identity.id) {
                                          return <td>Created</td>;
                                      }
                                      return <td></td>;
                                  },
                              } as IColumn,
                          ] as IColumn[])
                        : []),
                ]}
                itemActions={[
                    {
                        text: "Detail",
                        onClick: (item) => {
                            navigate(taskDetailPath(item.id));
                        },
                        primary: true,
                    } as IPagedListAction,
                ]}
                headActions={[
                    {
                        icon: faPlus,
                        text: "Add Task",
                        onClick: handleOpenAddTask,
                    } as IPagedListAction,
                ]}
                filters={[
                    {
                        title: "Task",
                        member: "name",
                        type: FilterType.text,
                    } as IFilter,
                    {
                        title: "Type",
                        member: "taskTypeName",
                        type: FilterType.text,
                    } as IFilter,
                    {
                        title: "Location",
                        member: "location",
                        type: FilterType.text,
                    } as IFilter,
                    {
                        title: "Task Status",
                        member: "taskStatusList",
                        type: FilterType.multiselect,
                        options: taskStatusObject,
                        defaultValue: [
                            { label: "Overdue", value: "Overdue" },
                            { label: "Open", value: "Open" },
                            { label: "DueToday", value: "DueToday" },
                        ],
                    } as IFilter,
                    ...dateRangeFilters("Created", "creationDateFrom", "creationDateTo"),
                    ...dateRangeFilters("Due", "dueDateFrom", "dueDateTo"),
                    ...(!admin
                        ? [
                              {
                                  title: "Created/Assigned",
                                  member: "assignmentKind",
                                  type: FilterType.select,
                                  options: {
                                      All: null,
                                      Created: "Created",
                                      Assigned: "Assigned",
                                      Both: "Both",
                                  },
                              } as IFilter,
                          ]
                        : []),
                    {
                        title: "Personnel",
                        member: "userName",
                        type: FilterType.text,
                    } as IFilter,
                ]}
                defaultSortBy="dueDate"
                getUrl={`/api/task/${admin ? "alltasks" : "all"}`}
                rowFormatter={() => {
                    return null;
                }}
                persistanceKey="TaskListNewPage"
                refreshNumber={refreshNumber + 1}
            />
            <TaskAddModel show={showAddTask} onClose={handleAddTask} />
        </Container>
    );
};

export { TaskListPage };
